import React, { Dispatch, SetStateAction, useCallback } from 'react';
import { Control, FieldValues, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import cn from 'classnames';

import { ChipsSelectMenuPlacement, DatepickerRange, RadioButton } from '@components/common';
import Select from '@components/common/Select/Select';

import { IDateInterval } from '@interfaces/kpi/kpiCommon';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { EDateSelectionModes, EDynamicDateRange, EFormFields } from '@enums/kpi-api';

import { dateOffsetOptions, invalidRangeError } from '@components/Kpi/Parameters/constants';

import '../Parameters.scss';

interface IKpiDatesParametersProps {
	control: Control;
	dateRangePreview: string;
	dynamicDatesOptions: TSelectOptionType<string>[];
	from: number | null;
	includeDateIntervalsFrom?: IDateInterval[];
	includeDateIntervalsTo?: IDateInterval[];
	isLoadingDynamicDates: boolean;
	isLoadingDateRangePreview: boolean;
	minDate: Date;
	register: UseFormRegister<FieldValues>;
	setFromDate: (date: number | null) => void;
	setToDate: (date: number | null) => void;
	to: number | null;
	watch: UseFormWatch<FieldValues>;
	dynamicId: string;
	staticId: string;
	setIsDirty?: Dispatch<SetStateAction<boolean>>;
	setValue: UseFormSetValue<FieldValues>;
	isRequired?: boolean;
}

const KpiDatesParameters = ({
	control,
	dateRangePreview,
	dynamicDatesOptions,
	from,
	includeDateIntervalsFrom,
	includeDateIntervalsTo,
	isLoadingDynamicDates,
	minDate,
	setFromDate,
	setToDate,
	to,
	watch,
	dynamicId,
	staticId,
	setIsDirty,
	setValue,
	isRequired = true,
}: IKpiDatesParametersProps) => {
	const dateSelectionMode = watch(EFormFields.dateSelectionMode);
	const dynamicDateRange = watch(EFormFields.dynamicDateRange)?.value;
	const startDateOffset = watch(EFormFields.startDateOffset)?.value;
	const dayLabel = startDateOffset === 1 ? 'day' : 'days';

	const isStatic = dateSelectionMode === EDateSelectionModes.static;
	const isCustomDateRange = dynamicDateRange === EDynamicDateRange.custom;
	const isShowDateRangePreview =
		!!dynamicDateRange &&
		(!isCustomDateRange ||
			(isCustomDateRange && startDateOffset !== undefined));

	const onChangeDateSelectionMode = useCallback((value: string) => {
		setValue(EFormFields.dateSelectionMode, value);

		if (setIsDirty) {
			setIsDirty(true);
		}
	}, [setIsDirty, setValue]);

	const onChangeDynamicDateRange = (value: TSelectOptionType<string>) => {
		setValue(EFormFields.dynamicDateRange, value);

		if (setIsDirty) {
			setIsDirty(true);
		}
	};

	const onChangeStartDateOffset = (value: TSelectOptionType<number>) => {
		setValue(EFormFields.startDateOffset, value);

		if (setIsDirty) {
			setIsDirty(true);
		}
	};

	return (
		<div className='f5gParametersDate'>
			<div className={cn('f5gParametersBodyLabel', {
				'f5gParametersBodyLabelRequired': isRequired,
			})}>
				Date range
			</div>
			<div>
				<RadioButton
					checked={isStatic}
					id={`${EDateSelectionModes.static}-${staticId}`}
					label='Static'
					name={`${EFormFields.dateSelectionMode}-${staticId}`}
					onChange={onChangeDateSelectionMode}
					value={EDateSelectionModes.static}
				/>
				{isStatic
					&& <DatepickerRange
						errorMessage={invalidRangeError}
						handleChangeFromDate={setFromDate}
						handleChangeToDate={setToDate}
						includeDateIntervalsFrom={includeDateIntervalsFrom}
						includeDateIntervalsTo={includeDateIntervalsTo}
						isClearableFrom
						isClearableTo
						isMobileDevice={false}
						minDateFrom={minDate}
						minDateTo={minDate}
						requiredFromDate={isRequired}
						requiredToDate={isRequired}
						value={{ from, to }}
					/>
				}
			</div>
			<div className='dynamicSection'>
				<div className='dynamicSelectionMode'>
					<RadioButton
						checked={!isStatic}
						id={`${EDateSelectionModes.dynamic}-${dynamicId}`}
						label='Dynamic:'
						name={`${EFormFields.dateSelectionMode}-${dynamicId}`}
						onChange={onChangeDateSelectionMode}
						value={EDateSelectionModes.dynamic}
					/>
					<Select
						control={control}
						disabled={isStatic}
						handleChange={onChangeDynamicDateRange}
						isLoading={isLoadingDynamicDates}
						menuPlacement={ChipsSelectMenuPlacement.top}
						minHeight='20px'
						name={EFormFields.dynamicDateRange}
						options={dynamicDatesOptions}
						placeholder='Select option'
						width='160px'
					/>
				</div>
				{isCustomDateRange
					&& <div className='dateOffsetOptionsSelect'>
						Last
						<Select
							control={control}
							disabled={isStatic}
							handleChange={onChangeStartDateOffset}
							menuPlacement={ChipsSelectMenuPlacement.top}
							name={EFormFields.startDateOffset}
							options={dateOffsetOptions}
							placeholder='number'
							width='100px'
						/>
						{dayLabel} from current date.
					</div>
				}
				{isShowDateRangePreview &&
					<div className='dateRangePreview'>
						{dateRangePreview}
					</div>
				}
			</div>
		</div>
	);
};

export default KpiDatesParameters;
